import React, { useState, useEffect, useRef } from "react";
import SkillCard from "../../components/Common/CommonCard";
import ReusableTable from "../../components/Common/BaseTable";
import productRatingIcon from "../../assets/productRating.png";
import { useNavigate } from "react-router-dom";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";
import ActionMenu from "../../components/ProductDescription/ActionMenu";
import { MoreHorizontal, RefreshCw } from "lucide-react";
import { fetchProducts, deleteProduct, refreshProducts } from "../../utils/Product";
import Breadcrumb from "../../components/Common/Bredcrump";
import { formatDate, searchProducts, sortProducts } from "../../helpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmDeleteModal from "../../components/ProductDescription/ConfirmDeleteModal";


const ProductDescription = () => {
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [showModal, setShowModal] = useState(false);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [productTags, setProductTags] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sku, setSku] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "created",
    order: "desc",
  });
  const [filterColumn, setFilterColumn] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [sourceFilter, setSourceFilter] = useState("All"); // "Amazon", "Shopify", "Local", "All"
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [perPage, setPerPage] = useState(50); // Default items per page
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // Debounced search term for API calls

  // Refs for AbortController and debounce timer
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Debounce search term - wait 400ms after user stops typing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400); // 400ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Use pagination for Amazon products
        const page = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? currentPage : 1;
        const limit = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? perPage : undefined;
        
        // Use refreshProducts for initial load to ensure fresh data
        // Pass debouncedSearchTerm to API if it exists
        const response = await refreshProducts(sourceFilter, limit, page, debouncedSearchTerm || null, abortController.signal);
        
        // Handle different response structures
        let productData;
        let paginationData = null;
        
        if (response && response.data && Array.isArray(response.data)) {
          productData = response.data;
          paginationData = response.pagination;
        } else if (response && Array.isArray(response)) {
          productData = response;
        } else {
          productData = [];
        }
        
        setProducts(productData);
        setFilteredProducts(productData);
        setPagination(paginationData);
        console.log("Initial product data loaded successfully:", productData.length, "products");
      } catch (error) {
        // Ignore errors from cancelled requests
        if (error.name === 'CanceledError' || error.name === 'AbortError' || 
            error.code === 'ERR_CANCELED' || error.message === 'canceled') {
          console.log("Request was cancelled");
          return;
        }
        
        console.error("Error fetching products:", error);
        // Fall back to regular fetch if refresh fails
        try {
          const page = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? currentPage : 1;
          const limit = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? perPage : undefined;
          
          const response = await fetchProducts(sourceFilter, limit, page, debouncedSearchTerm || null, abortController.signal);
          
          // Handle different response structures for fallback
          let productData;
          let paginationData = null;
          
          if (response && response.data && Array.isArray(response.data)) {
            productData = response.data;
            paginationData = response.pagination;
          } else if (response && Array.isArray(response)) {
            productData = response;
          } else {
            productData = [];
          }
          
          setProducts(productData);
          setFilteredProducts(productData);
          setPagination(paginationData);
          console.log("Fallback product data loaded successfully:", productData.length, "products");
        } catch (fallbackError) {
          // Ignore errors from cancelled requests
          if (fallbackError.name === 'CanceledError' || fallbackError.name === 'AbortError' || (fallbackError.code === 'ERR_CANCELED')) {
            console.log("Fallback request was cancelled");
            return;
          }
          
          console.error("Error fetching products (fallback):", fallbackError);
          // Set empty array as final fallback
          setProducts([]);
          setFilteredProducts([]);
          setPagination(null);
        }
      } finally {
        // Only update loading state if this request wasn't cancelled
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchData();

    // Cleanup function - cancel request on unmount or when dependencies change
    return () => {
      abortController.abort();
    };
  }, [sourceFilter, currentPage, perPage, debouncedSearchTerm]);

  // Reset to page 1 when source filter changes
  useEffect(() => {
    setCurrentPage(1);
    setPerPage(50); // Reset per page to default
  }, [sourceFilter]);

  // Cleanup: Cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      // Cancel pending API request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Ensure products is always an array before processing
    if (!Array.isArray(products)) {
      console.warn("Products is not an array, setting to empty array:", products);
      setProducts([]);
      setFilteredProducts([]);
      return;
    }

    let result = [...products];

    // Apply source filter locally (as backup if API doesn't filter)
    // Only filter if sourceFilter is not "All"
    if (sourceFilter && sourceFilter !== "All" && sourceFilter.toLowerCase() !== "all") {
      result = result.filter(product => {
        const productSource = product.source || product.platform || "";
        return productSource.toLowerCase() === sourceFilter.toLowerCase();
      });
    }

    // Search is now handled by API, so we don't filter here
    // if (searchTerm) {
    //   result = searchProducts(result, searchTerm);
    // }

    if (filterColumn && filterValue) {
      result = result.filter(item => {
        const itemValue = item[filterColumn];
        if (!itemValue) return false;
        
        // Special handling for date filtering
        if (filterColumn === 'created_at') {
          const rawDate = item.created_at;
          const formattedDate = formatDate(rawDate);
          
          const searchLower = filterValue.toLowerCase();
          const rawDateStr = rawDate ? new Date(rawDate).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
          }).toLowerCase() : '';
          const formattedDateStr = formattedDate ? formattedDate.toLowerCase() : '';
          
          return rawDateStr.includes(searchLower) || formattedDateStr.includes(searchLower);
        }
        
        return itemValue.toString().toLowerCase().includes(filterValue.toLowerCase());
      });
    }

    result = sortProducts(result, sortConfig.key, sortConfig.order);

    setFilteredProducts(result);
  }, [products, sortConfig, filterColumn, filterValue, sourceFilter]);


  const handleSearch = (searchValue) => {
    // When user searches, automatically switch to "All" source filter
    if (searchValue && searchValue.trim()) {
      setSourceFilter("All");
    }
    setSearchTerm(searchValue);
  };

  const handleSort = (sortOption) => {
    switch (sortOption) {
      case "newest":
        setSortConfig({ key: "created", order: "desc" });
        break;
      case "oldest":
        setSortConfig({ key: "created", order: "asc" });
        break;
      default:
        setSortConfig({ key: "created", order: "desc" });
    }
  };

  const handleFilter = (column, value) => {
    if (column) {
      setFilterColumn(column);
    }
    if (value !== undefined) {
      setFilterValue(value || '');
    }
  };

  const handleDelete = (selectedRowIds) => {
    if (!selectedRowIds || selectedRowIds.length === 0) {
      toast.warning("No rows selected for deletion");
      return;
    }
    setPendingDeleteIds(Array.isArray(selectedRowIds) ? selectedRowIds : [selectedRowIds]);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    const selectedRowIds = pendingDeleteIds;
    setPendingDeleteIds([]);
    try {
      const deletePromises = selectedRowIds.map(async (productId) => {
        try {
          await deleteProduct(productId);
          return { success: true, id: productId };
        } catch (error) {
          console.error(`Error deleting product ${productId}:`, error);
          return { success: false, id: productId, error: error.message };
        }
      });
      const results = await Promise.all(deletePromises);
      const successful = results.filter(result => result.success);
      const failed = results.filter(result => !result.success);
      if (successful.length > 0) {
        toast.success(`Successfully deleted ${successful.length} product(s)`);
        const deletedIds = successful.map(result => result.id.toString());
        setProducts(prevProducts => 
          prevProducts.filter(product => !deletedIds.includes(product.id.toString()))
        );
      }
      if (failed.length > 0) {
        toast.error(`Failed to delete ${failed.length} product(s). Please try again.`);
      }
    } catch (error) {
      console.error("Error in confirmDelete:", error);
      toast.error("An error occurred while deleting products. Please try again.");
    }
  };

  const handleDuplicate = (selectedRowIds) => {
    if (!selectedRowIds || selectedRowIds.length === 0) {
      toast.warning("No rows selected for duplication");
      return;
    }
    
    // Convert row IDs to actual row objects
    const selectedRows = filteredProducts.filter(product => 
      selectedRowIds.includes(product.id.toString())
    );
    
    if (selectedRows.length === 0) {
      toast.warning("No valid products found for duplication");
      return;
    }
    
    console.log("Duplicate:", selectedRows);
    toast.info("Duplicate functionality not implemented yet");
  };

  const handleAction = (action, document) => {
    setActionMenuOpen(null);

    if (action === "edit") {
      const newSku = document.product_sku || 
        (document.variants && document.variants[0] && document.variants[0].sku) || 
        document.title.replaceAll(" ", "_");
      setSku(newSku);
      setProductTags(document.tags || "");
      setSelectedDocument(document);
      setShowModal(true);
    }
    if (action === "delete") {
      handleDelete(document.id);
    }
    if (action === "preview") {
      // Get user ID from localStorage and encode it (base64 encoding) - only for products
      const userId = localStorage.getItem('user_id');
      let encodedUserId = '';
      if (userId) {
        // Encode user ID using base64
        encodedUserId = btoa(userId.toString());
      }
      
      // Build preview URL with encoded user_id
      let previewUrl = `/preview-product-description/${document.id}`;
      if (encodedUserId) {
        previewUrl += `?user_id=${encodedUserId}`;
      }
      
      navigate(previewUrl, {
        state: {
          contentType: "product",
          backUrl: "/skills/product-description",
          productSource: document.source || "local" // Pass source to determine which API to call
        }
      });
    }
    if (action === "share") {
      // Share functionality for local platform products
      if (!document.id) {
        toast.warning('Cannot share: Product ID not available');
        return;
      }
      
      // Get user ID from localStorage and encode it (base64 encoding) - only for products
      const userId = localStorage.getItem('user_id');
      let encodedUserId = '';
      if (userId) {
        // Encode user ID using base64
        encodedUserId = btoa(userId.toString());
      }
      
      // Create share URL with encoded user_id
      let shareUrl = `${window.location.origin}/preview-product-description/${document.id}`;
      if (encodedUserId) {
        shareUrl += `?user_id=${encodedUserId}`;
      }
      
      // Copy to clipboard with fallback support
      const copyToClipboard = async (text) => {
        // Check if modern Clipboard API is available
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(text);
            return;
          } catch (err) {
            // If Clipboard API fails, fall back to execCommand
            console.warn('Clipboard API failed, trying fallback method:', err);
          }
        }

        // Fallback method using execCommand (works in more environments)
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (!successful) {
            throw new Error('execCommand copy failed');
          }
        } catch (err) {
          console.error('Failed to copy text to clipboard:', err);
          throw err;
        }
      };

      copyToClipboard(shareUrl)
        .then(() => {
          toast.success('Share link copied to clipboard!');
        })
        .catch(() => {
          toast.error('Failed to copy link. Please copy manually: ' + shareUrl);
        });
    }
  };

  const skills = [{ title: "Product Description", icon: productRatingIcon }];

  // Action Cell Component
  const ActionCell = ({ row, actionMenuOpen, setActionMenuOpen, handleAction }) => {
    const buttonRef = useRef(null);
    
    return (
      <div className="position-relative">
        <button
          ref={buttonRef}
          className="action-button btn"
          onClick={(e) => {
            e.stopPropagation();
            setActionMenuOpen(
              actionMenuOpen === row.original.id ? null : row.original.id
            );

            document.addEventListener(
              "click",
              () => {
                setActionMenuOpen(null);
              },
              { once: true }
            );
          }}
        >
          <MoreHorizontal size={20} />
        </button>
        {actionMenuOpen === row.original.id && (
          <ActionMenu
            onAction={(action) => handleAction(action, row.original)}
            document={row.original}
            buttonRef={buttonRef}
          />
        )}
      </div>
    );
  };

  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
    },
    {
      accessorKey: "product_name",
      header: "Name",
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "source",
      header: "Platform",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        const capitalizedStatus = status.charAt(0).toUpperCase() + status.slice(1);
        const statusClass =
          status === "active" ? "status-published" : "status-draft";
        const dotColor = status === "active" ? "green" : "grey";
    
        return (
          <span className={`status-badge ${statusClass}`}>
            <span style={{ color: dotColor, marginRight: "5px" }}>•</span>
            {capitalizedStatus}
          </span>
        );
      },
    },
    
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionCell
          row={row}
          actionMenuOpen={actionMenuOpen}
          setActionMenuOpen={setActionMenuOpen}
          handleAction={handleAction}
        />
      ),
    },
  ];

  const handleSkillClick = (title) => {
    console.log("HandleSkillClick");
    navigate("/product-description");
  };

  // Function to refresh product data
  const refreshProductData = async () => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    try {
      const page = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? currentPage : 1;
      const limit = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? perPage : undefined;
      
      // Use debouncedSearchTerm for consistency with automatic fetches
      const response = await refreshProducts(sourceFilter, limit, page, debouncedSearchTerm || null, abortController.signal);
      
      // Handle different response structures
      let productData;
      let paginationData = null;
      
      if (response && response.data && Array.isArray(response.data)) {
        productData = response.data;
        paginationData = response.pagination;
      } else if (response && Array.isArray(response)) {
        productData = response;
      } else {
        productData = [];
      }
      
      setProducts(productData);
      setFilteredProducts(productData);
      setPagination(paginationData);
      console.log("Product data refreshed successfully:", productData.length, "products");
    } catch (error) {
      // Ignore errors from cancelled requests
      if (error.name === 'CanceledError' || error.name === 'AbortError' || (error.code === 'ERR_CANCELED')) {
        console.log("Refresh request was cancelled");
        return;
      }
      
      console.error("Error refreshing products:", error);
      // Fall back to regular fetch if refresh fails
      try {
        const page = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? currentPage : 1;
        const limit = sourceFilter && sourceFilter.toLowerCase() === 'amazon' ? perPage : undefined;
        
        const response = await fetchProducts(sourceFilter, limit, page, debouncedSearchTerm || null, abortController.signal);
        
        // Handle different response structures for fallback
        let productData;
        let paginationData = null;
        
        if (response && response.data && Array.isArray(response.data)) {
          productData = response.data;
          paginationData = response.pagination;
        } else if (response && Array.isArray(response)) {
          productData = response;
        } else {
          productData = [];
        }
        
        setProducts(productData);
        setFilteredProducts(productData);
        setPagination(paginationData);
        console.log("Fallback product data loaded successfully:", productData.length, "products");
      } catch (fallbackError) {
        // Ignore errors from cancelled requests
        if (fallbackError.name === 'CanceledError' || fallbackError.name === 'AbortError' || (fallbackError.code === 'ERR_CANCELED')) {
          console.log("Fallback refresh request was cancelled");
          return;
        }
        
        console.error("Error fetching products (fallback):", fallbackError);
        // Set empty array as final fallback
        setProducts([]);
        setFilteredProducts([]);
        setPagination(null);
      }
    } finally {
      // Only update loading state if this request wasn't cancelled
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  // Check if product list needs refresh based on localStorage flag
  useEffect(() => {
    const needsRefresh = localStorage.getItem('refreshProductList');
    if (needsRefresh === 'true') {
      localStorage.removeItem('refreshProductList');
      refreshProductData();
    }
  }, []);

  // Add window focus listener to refresh data when user returns to page
  // COMMENTED OUT: Auto-refresh was too annoying when switching tabs
  // useEffect(() => {
  //   const handleWindowFocus = () => {
  //     // Refresh product data when window gains focus
  //     refreshProductData();
  //   };

  //   window.addEventListener('focus', handleWindowFocus);
    
  //   return () => {
  //     window.removeEventListener('focus', handleWindowFocus);
  //   };
  // }, []);

  return (
    <div>
      <style>
        {`
          .spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <Breadcrumb />

      <div
        className="skills-container"
        style={{ display: "flex", gap: "16px", marginTop: "5px" }}
      >
        {skills.map((skill, index) => (
          <SkillCard
            key={skill?.title}
            icon={skill.icon}
            title={skill.title}
            onClick={() => handleSkillClick(skill.title)}
          />
        ))}
      </div>
      <div className="title-table-header">Generated Content</div>
      <div className="mt-3" style={{ marginBottom: '16px' }}>
        <div className="d-flex align-items-center gap-2" style={{ marginBottom: '16px' }}>
          <label htmlFor="source-filter" style={{ marginBottom: 0, fontWeight: 500 }}>
            Filter by Platform:
          </label>
          <select
            id="source-filter"
            className="form-select"
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
            }}
            style={{ width: 'auto', minWidth: '150px' }}
          >
            <option value="All">All</option>
            <option value="Amazon">Amazon</option>
            <option value="Shopify">Shopify</option>
            <option value="Local">Local</option>
          </select>
        </div>
        <ReusableTable
          data={filteredProducts}
          columns={columns}
          onSearch={handleSearch}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onSort={handleSort}
          onFilter={handleFilter}
          gridView={false}
          isLoading={isLoading}
          filterColumn={filterColumn}
          setFilterColumn={setFilterColumn}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          filterOptions={[
            { value: "product_name", label: "Name" },
            { value: "source", label: "Platform" },
            { value: "status", label: "Status" },
            { value: "created_at", label: "Created" },
          ]}
          // Server-side pagination for Amazon products
          useServerPagination={sourceFilter && sourceFilter.toLowerCase() === 'amazon' && pagination !== null}
          page={sourceFilter && sourceFilter.toLowerCase() === 'amazon' && pagination ? pagination.current_page : undefined}
          perPage={sourceFilter && sourceFilter.toLowerCase() === 'amazon' && pagination ? pagination.per_page : undefined}
          total={sourceFilter && sourceFilter.toLowerCase() === 'amazon' && pagination ? pagination.total_items : undefined}
          onPageChange={(newPage) => {
            if (sourceFilter && sourceFilter.toLowerCase() === 'amazon') {
              setCurrentPage(newPage);
            }
          }}
          onPerPageChange={(newPerPage) => {
            if (sourceFilter && sourceFilter.toLowerCase() === 'amazon') {
              setPerPage(newPerPage);
              setCurrentPage(1); // Reset to first page when changing per page
            }
          }}
          customToolbarActions={
            <button
              className="btn btn-outline-secondary btn-sm d-flex align-items-center"
              onClick={refreshProductData}
              disabled={isLoading}
              title="Refresh products list"
              style={{ 
                border: '1px solid #dee2e6',
                color: '#6c757d',
                backgroundColor: 'transparent',
                marginLeft: '8px'
              }}
            >
              <RefreshCw 
                size={16} 
                className={isLoading ? 'spin' : ''} 
                style={{ marginRight: '4px' }}
              />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          }
        />
      </div>
      <UnifiedDocumentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        initialTags={productTags}
        sku={sku}
        productData={selectedDocument}
      />
      <ConfirmDeleteModal
        show={showDeleteModal}
        onConfirm={confirmDelete}
        onCancel={() => { setShowDeleteModal(false); setPendingDeleteIds([]); }}
        count={pendingDeleteIds.length}
      />
    </div>
  );
};

export default ProductDescription;
